import { Component, AfterViewInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { Title, Meta } from '@angular/platform-browser';
import { VideoCarouselComponent, VideoItem } from '../../components/video-carousel/video-carousel';
import { DataService } from '../../services/data';

declare var Appfolio: any;

@Component({
  selector: 'app-home',
  imports: [RouterLink, VideoCarouselComponent, AsyncPipe],
  template: `
<!-- Hero Section -->
<div class="p-5 m-4 bg-dark text-white rounded-3">
  <div class="container-fluid py-5 text-center">
    <h1 class="display-4 fw-bold">Welcome to <span style="color: #F1B82D;">Rent</span>MU.com</h1>
    <p class="col-md-8 mx-auto fs-4">Your first choice in student housing for the University of Missouri, Columbia College, and Stephens College.</p>
    <a (click)="scrollToListings($event)" href="#appfolio_listing_container" class="btn btn-secondary btn-lg px-4 gap-3">Apply Now</a>
  </div>
</div>

<!-- College Logos Section -->
<div class="container mb-5 logos">
  <div class="row align-items-center text-center">
    <div class="col-md-4 mb-3 mb-md-0">
      <img src="img/mu.png" alt="University of Missouri" class="img-fluid" style="max-height: 80px;">
    </div>
    <div class="col-md-4 mb-3 mb-md-0">
      <img src="img/cc.png" alt="Columbia College" class="img-fluid" style="max-height: 80px;">
    </div>
    <div class="col-md-4">
      <img src="img/stephens.png" alt="Stephens College" class="img-fluid" style="max-height: 80px;">
    </div>
  </div>
</div>

<div class="container marketing">
  @if (galleryItems$ | async; as galleryItems) {
    <div class="row mb-5">
      <div class="col-12" id="videoRow">
        <h2 class="mb-4 text-center">Take a Virtual Tour</h2>
        <app-video-carousel [items]="galleryItems"></app-video-carousel>
      </div>
    </div>
  }

  <hr class="featurette-divider">

  <!-- AppFolio Listing Widget 1 -->
  <div class="row my-5">
    <div class="col-12 listingRow">
      <h2 class="mb-4 text-center">Available Listings - RentMU</h2>
      <div id="appfolio_listing_container" class="border rounded p-2 bg-light"></div>
    </div>
  </div>

  <!-- AppFolio Listing Widget 2 -->
  <div class="row my-5">
    <div class="col-12 listingRow">
      <h2 class="mb-4 text-center">Available Listings - Other</h2>
      <div id="appfolio_listing_container_2" class="border rounded p-2 bg-light"></div>
    </div>
  </div>

  <hr class="featurette-divider">

  <!-- Featurettes -->
  <!-- Featurette 1 -->
  <div class="row featurette align-items-center">
    <div class="col-md-7">
      <h2 class="featurette-heading fw-normal lh-1">Close to campus! <span class="text-muted">Close to everything!</span></h2>
      <p class="lead">Windsor Place is situated adjacent to the University of Missouri, Columbia College and Stephens College campuses. Whatever your institution of higher learning, you're just a step away.</p>
    </div>
    <div class="col-md-5">
      <a routerLink="/locations">
        <img class="featurette-image img-fluid mx-auto rounded shadow" src="img/win1.jpg" alt="Windsor Place">
      </a>
    </div>
  </div>

  <hr class="featurette-divider">

  <!-- Featurette 2 -->
  <div class="row featurette align-items-center">
    <div class="col-md-7 order-md-2">
      <h2 class="featurette-heading fw-normal lh-1">A coveted location. <span class="text-muted">That won't kill your wallet.</span></h2>
      <p class="lead">Rock Bridge Estates is located approximately 3 miles south of the Mizzou campus in desirable South Columbia. Enjoy this sought-after location without the luxury pricing.</p>
    </div>
    <div class="col-md-5 order-md-1">
      <a routerLink="/locations">
        <img class="featurette-image img-fluid mx-auto rounded shadow" src="img/1c.jpg" alt="Rock Bridge Estates">
      </a>
    </div>
  </div>

  <hr class="featurette-divider">

  <!-- Featurette 3 -->
  <div class="row featurette align-items-center mb-5">
    <div class="col-md-7">
      <h2 class="featurette-heading fw-normal lh-1">Building a community. <span class="text-muted">As members of the community.</span></h2>
      <p class="lead">Why put up with absentee landlords or investment groups that don't care about your personal interests? All of our friendly staff and owners are located right here in Columbia, striving to build a better community, because it's <i>our</i> community.</p>
    </div>
    <div class="col-md-5">
      <a routerLink="/apply">
        <img class="featurette-image img-fluid mx-auto rounded shadow" src="img/mb.jpg" alt="Owners Bill Baker and Matt Hake">
      </a>
    </div>
  </div>
</div>
  `,
  styles: `
    .logos {
      margin-top: 3rem !important;
    }
    .featurette-divider {
      margin: 5rem 0;
    }
    .featurette-heading {
      font-size: 50px;
    }
    .listingRow .border {
      border: 1px solid #dee2e6 !important;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    }
    @media (max-width: 768px) {
      .featurette-heading {
        font-size: 30px;
      }
    }
  `,
})
export class Home implements AfterViewInit {
  galleryItems$: Observable<VideoItem[]>;

  constructor(private dataService: DataService, private titleService: Title, private metaService: Meta) {
    this.galleryItems$ = this.dataService.getHomeGallery();
    this.titleService.setTitle('RentMU.com - Student Housing in Columbia, MO');
    this.metaService.updateTag({ name: 'description', content: 'Welcome to RentMU.com, your first choice in student housing for University of Missouri, Columbia College and Stephens College.' });
  }

  scrollToListings(event: MouseEvent) {
    event.preventDefault();
    const element = document.getElementById('appfolio_listing_container');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  ngAfterViewInit() {
    if (typeof Appfolio !== 'undefined' && Appfolio.Listing) {
      const widgets = [
        { id: 'appfolio_listing_container', host: 'hakerproperties.appfolio.com' },
        { id: 'appfolio_listing_container_2', host: 'rentmu.appfolio.com' }
      ];

      const oldWrite = document.write;

      widgets.forEach(widget => {
        const container = document.getElementById(widget.id);
        if (container) {
          document.write = (content: string) => {
            container.innerHTML = content;
          };

          Appfolio.Listing({
            hostUrl: widget.host,
            targetId: widget.id,
            themeColor: '#212529',
            themeFont: 'Armata, sans-serif',
            linkTextColor: '#F1B82D',
            height: 'auto',
            width: '100%',
            defaultOrder: 'date_posted'
          });
        }
      });

      // Restore original document.write
      document.write = oldWrite;
    }
  }
}
